import { MoreHoriz, SpeakerNotesOff } from "@mui/icons-material";
import {
  Badge,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Chat as ChatModel, GroupChat, Profile } from "@readyfit-common/models";
import AthleteIcon from "src/grading/components/AthleteIcon/AthleteIcon";
import LoadingOverlay from "src/grading/components/LoadingOverlay/LoadingOverlay";
import { getRelativeTime } from "src/grading/utils";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import theme from "src/theme";

interface ChatListProps {
  chats?: ChatModel[] | GroupChat[];
  isLoading: boolean;
  selectedMessage: ChatModel | null;
  selectedGroupMessage: GroupChat | null;
  onSelectMessage: (chat: any) => void;
  isGroup: boolean;
  handleOpenDeleteGroupChatModal: (groupChat: GroupChat) => void;
  handleOpenLeaveGroupChatModal: (groupChat: any) => void;
}

export default function ChatList({
  chats,
  isLoading,
  selectedMessage,
  selectedGroupMessage,
  onSelectMessage,
  isGroup,
  handleOpenDeleteGroupChatModal,
  handleOpenLeaveGroupChatModal,
}: ChatListProps) {
  const profile: Profile = useAppSelector(selectCurrentUser);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChatIndex, setSelectedChatIndex] = useState<number | null>(
    null,
  );

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedChatIndex(index);
  };

  const handleCloseMenu = (
    event: object,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick") {
      // Cast to access stopPropagation method
      (event as any)?.stopPropagation?.();
    }
    setAnchorEl(null);
    setSelectedChatIndex(null);
  };

  return (
    <Grid item sx={{ flex: 1, overflow: "auto" }}>
      {isLoading ? (
        <LoadingOverlay />
      ) : chats?.length === 0 ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: "50%" }}
          flexDirection="column"
          gap={2}
        >
          <SpeakerNotesOff />
          <Typography variant="subtitle1">No chats available</Typography>
        </Grid>
      ) : (
        chats?.map((chat, i) => {
          const isGroupChat = isGroup && "id" in chat;
          const isChatModel = !isGroup && "userId" in chat;
          const isGroupChatOwner =
            isGroupChat && (chat as GroupChat).ownerId === profile.id;
          const isGroupChatMember =
            isGroupChat && (chat as GroupChat).member && !isGroupChatOwner;

          return (
            <Badge
              key={`${chat.name}-${i}`}
              badgeContent={
                isChatModel && (chat as ChatModel).unreadCount > 0
                  ? (chat as ChatModel).unreadCount
                  : null
              }
              color="error"
              sx={{
                width: "100%",
                "& .MuiBadge-badge": {
                  right: theme.spacing(2),
                  top: theme.spacing(2),
                },
              }}
            >
              <Paper
                sx={{
                  padding: theme.spacing(2),
                  marginBottom: theme.spacing(1),
                  cursor: "pointer",
                  border: "1px solid #C2C2C2",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  background:
                    selectedMessage &&
                    isChatModel &&
                    selectedMessage?.lastMessageDate ===
                      (chat as ChatModel).lastMessageDate
                      ? "#C5D9CF"
                      : selectedGroupMessage &&
                          isGroupChat &&
                          selectedGroupMessage?.id === (chat as GroupChat).id
                        ? "#C5D9CF"
                        : "white",
                  width: "100%",
                }}
                onClick={() => onSelectMessage(chat)}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <AthleteIcon
                      profile={{ firstName: chat.name || "" } as Profile}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="caption" fontWeight={"bold"}>
                      {chat.name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.primary">
                      {isChatModel &&
                        (chat as ChatModel).lastMessageDate &&
                        getRelativeTime(
                          new Date(
                            (chat as ChatModel).lastMessageDate,
                          ).getTime(),
                        )}
                      {isGroupChat &&
                        getRelativeTime(
                          new Date((chat as GroupChat).createdAt).getTime(),
                        )}
                    </Typography>
                    {isGroupChat && !isGroupChatMember && !isGroupChatOwner && (
                      <Typography variant="button" fontSize={12}>
                        Not a Member
                      </Typography>
                    )}
                  </Grid>
                  {isGroup && (isGroupChatOwner || isGroupChatMember) && (
                    <Grid item>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMenu(e, i);
                        }}
                      >
                        <MoreHoriz />
                      </IconButton>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedChatIndex === i}
                        onClose={handleCloseMenu}
                        MenuListProps={{
                          "aria-labelledby": "weekly-button",
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: theme.spacing(1),
                              padding: theme.spacing(0.5),
                            },
                          },
                        }}
                      >
                        {isGroupChatOwner && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteGroupChatModal(chat as GroupChat);
                              handleCloseMenu({} as any, "backdropClick");
                            }}
                          >
                            <Typography variant="subtitle1">Delete</Typography>
                          </MenuItem>
                        )}
                        {isGroupChatMember && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenLeaveGroupChatModal(chat as GroupChat);
                              handleCloseMenu({} as any, "backdropClick");
                            }}
                          >
                            <Typography variant="subtitle1">Leave</Typography>
                          </MenuItem>
                        )}
                      </Menu>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Badge>
          );
        })
      )}
    </Grid>
  );
}
